import { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';

import { FOCUS_THROTTLE_INTERVAL } from '@/config/appSettings';

import { IResponseError } from '@/lib/apiBase';
import { IRepository } from '@/types';
import {
  IMarimoSandbox,
  getMarimoSandboxes,
  deleteMarimoSandbox,
  getMarimoSandbox,
  IMarimoSandboxesResponse,
} from '@/lib/marimoSandboxesApi';

interface UseNotebooksParams {
  repository: IRepository;
  initialData?: IMarimoSandbox[];
}

const useNotebooks = ({ repository, initialData = [] }: UseNotebooksParams) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [error, setError] = useState<IResponseError | undefined>();
  const [notebooks, setNotebooks] = useState<IMarimoSandbox[]>(initialData);

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    ['notebooks', repository.namespace, repository.name],
    () => getMarimoSandboxes(repository.namespace, repository.name),
    {
      keepPreviousData: true,
      focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
      fallbackData: initialData?.length
        ? ({ status: 'success', notebooks: initialData } as IMarimoSandboxesResponse)
        : undefined,
    },
  );

  useEffect(() => {
    if (data) {
      setNotebooks(data.notebooks || []);
      setStatus(data.status);
      setError(data.error as IResponseError);
    }
  }, [data]);

  const stopNotebook = useCallback(
    async (notebookId: string) => {
      try {
        const response = await deleteMarimoSandbox(repository.namespace, repository.name, notebookId);

        if (response.status === 'success') {
          // Optimistically update the UI
          setNotebooks((current) => current.filter((notebook) => notebook.id !== notebookId));
          // Revalidate data
          mutate();
        } else {
          console.error('Failed to delete notebook:', response.error);
        }

        return response;
      } catch (error) {
        console.error('Error deleting notebook:', error);
        return { status: 'error', error };
      }
    },
    [repository, mutate],
  );

  return {
    status,
    isLoading,
    isValidating,
    notebooks,
    error,
    stopNotebook,
  };
};

export const useNotebookById = ({ repository, notebookId }: { repository: IRepository; notebookId: string }) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [error, setError] = useState<IResponseError | undefined>();
  const [notebook, setNotebook] = useState<IMarimoSandbox | undefined>();

  const {
    data,
    error: requestError,
    isLoading,
    isValidating,
  } = useSWR(
    ['marimo_sandbox', repository.namespace, repository.name, notebookId],
    () => getMarimoSandbox(repository.namespace, repository.name, notebookId),
    {
      keepPreviousData: true,
      focusThrottleInterval: FOCUS_THROTTLE_INTERVAL,
    },
  );

  useEffect(() => {
    if (data) {
      setNotebook(data.notebook);
      setStatus(data.status);
      setError(data.error as IResponseError);
    }
  }, [data]);

  return {
    status,
    isLoading,
    isValidating,
    notebook,
    error,
  };
};

export default useNotebooks;
